import React, { useId, useState } from 'react';
import { AlertCircle, ChevronDown, ChevronRight, Pause, Play, Save, Trash2 } from 'lucide-react';
import { getTournamentType } from '../../engine';
import { useT } from '../../i18n';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

// The three confirmations' backdrop, moved verbatim from their hand-built
// overlays onto `Dialog` (#900).
const CONFIRM_BACKDROP_STYLE = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  background: 'rgba(15, 22, 17, 0.55)',
} as const;

export interface PausedTournamentsCardProps {
  /** Paused / parked tournaments to list (running ones are split out automatically). */
  pausedTournaments: any[];
  /** The currently-open tournament, shown as "Åpen nå" at the top; null when none. */
  activeTournament?: any;
  /** Re-opens the active tournament. */
  onOpenActive?: (() => void) | null;
  /** Saves the active tournament to the archive and closes it. */
  onArchiveActive?: (() => void) | null;
  /** Deletes the active tournament. */
  onDeleteActive?: (() => void) | null;
  /** Resumes / opens a listed tournament by id. */
  onResume: (id: any) => void;
  /** Pauses a running listed tournament by id. */
  onPauseRunning?: ((id: any) => void) | null;
  /** Archives a listed tournament by id. */
  onArchiveFromList?: ((id: any) => void) | null;
  /** Deletes a listed tournament by id. */
  onDelete: (id: any) => void;
  /** Deletes all paused tournaments. */
  onDeleteAll: () => void;
}

/**
 * Home-surface card listing the user's running + paused tournaments (plus the
 * currently-open one), each resumable/archivable/deletable with inline confirm
 * dialogs. Extracted verbatim (byte-for-byte body) from the monolith
 * (legacy/App.jsx). Pure leaf — renders only HTML + lucide icons and local
 * useState; the two `Padel24Engine.getTournamentType(...)` calls are swapped to
 * the named `getTournamentType` import from the engine barrel (the same function
 * the monolith's Padel24Engine wiring delegated to).
 */
export function PausedTournamentsCard({
  pausedTournaments,
  activeTournament = null,
  onOpenActive = null,
  onArchiveActive = null,
  onDeleteActive = null,
  onResume,
  onPauseRunning = null,
  onArchiveFromList = null,
  onDelete,
  onDeleteAll,
}: PausedTournamentsCardProps) {
  // `tr`, not `t`: `t` is the tournament in every callback below.
  const tr = useT();
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [pausedOpen, setPausedOpen] = useState(false); // paused ones are minimized by default
  const [confirmArchiveActive, setConfirmArchiveActive] = useState(null); // confirm «save and close» on an unfinished tournament
  // Ids that name and describe the three confirmation dialogs (#900).
  const uid = safeId('paused-tournaments', useId());
  const dlg = {
    deleteTitle: `${uid}-delete-title`,
    deleteBody: `${uid}-delete-body`,
    archiveTitle: `${uid}-archive-title`,
    archiveBody: `${uid}-archive-body`,
    deleteAllTitle: `${uid}-delete-all-title`,
    deleteAllBody: `${uid}-delete-all-body`,
  };

  if (!pausedTournaments?.length && !activeTournament) return null;

  const formatPausedSince = (ts) => {
    if (!ts) return null;
    const diff = Date.now() - ts;
    const min = Math.floor(diff / 60000);
    if (min < 1) return tr('home.common.ago.justNow');
    if (min < 60) return tr('home.common.ago.minutes', { min: String(min) });
    const hr = Math.floor(min / 60);
    if (hr < 24)
      return tr(hr === 1 ? 'home.common.ago.hour' : 'home.common.ago.hours', {
        count: String(hr),
      });
    const d = Math.floor(hr / 24);
    return tr(d === 1 ? 'home.common.ago.day' : 'home.common.ago.days', {
      count: String(d),
    });
  };

  const count = pausedTournaments.length;
  const isRunningStatus = (s) => s === 'running' || s === 'playing' || s === 'finals';
  const runningList = pausedTournaments.filter((t) => isRunningStatus(t.status));
  const pausedList = pausedTournaments.filter((t) => !isRunningStatus(t.status));

  const renderCard = (t, running) => {
    const tt = getTournamentType(t.config);
    const completedCount = (t.matches || []).filter((m) => m.completed).length;
    const totalCount = (t.matches || []).length;
    const isAmericano =
      t.type === 'americano' ||
      t.type === 'mexicano' ||
      t.type === 'mixicano' ||
      t.type === 'team_mexicano' ||
      t.type === 'mix_americano' ||
      t.type === 'club_americano' ||
      t.type === 'club_team_americano' ||
      t.type === 'club_mexicano';
    const since = formatPausedSince(t.pausedAt);
    return (
      <div
        key={t.id}
        className="card-elev p-4 border-l-4 flex items-center gap-3 flex-wrap"
        style={{
          borderLeftColor: tt.color,
          background: `linear-gradient(135deg, ${tt.color}14 0%, ${tt.color}2e 100%)`,
        }}
      >
        <span
          className="font-mono text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0"
          style={{ background: tt.color + '40', color: '#0f1611' }}
          title={tt.label}
        >
          {tt.abbrev}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base font-semibold text-ink truncate">
            {t.config?.name || tr('home.paused.untitled')}
          </div>
          <div className="text-[11px] text-ink-3 font-mono flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 items-center">
            <span>{tr('home.paused.players', { count: String(t.players?.length || 0) })}</span>
            <span>
              {totalCount > 0
                ? tr('home.paused.matchesPlayed', {
                    done: String(completedCount),
                    total: String(totalCount),
                  })
                : tr('home.paused.notStarted')}
            </span>
            {isAmericano && t.currentRound > 0 && (
              <span>{tr('home.paused.round', { round: String(t.currentRound) })}</span>
            )}
            {running ? (
              <span
                className="inline-flex items-center gap-1 font-bold"
                style={{ color: '#15803d' }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: '#22c55e',
                    display: 'inline-block',
                  }}
                />
                {t.status === 'finals'
                  ? tr('home.paused.placementMatches')
                  : tr('home.paused.inProgress')}
              </span>
            ) : (
              since && <span>{tr('home.paused.pausedSince', { since })}</span>
            )}
          </div>
        </div>
        <div className="flex gap-1.5 flex-shrink-0">
          <button
            onClick={() => onResume(t.id)}
            className="btn-lime text-sm"
            title={tr(running ? 'home.paused.reopenTitle' : 'home.paused.resumeTitle')}
          >
            <Play size={12} /> {tr(running ? 'home.paused.open' : 'home.paused.resume')}
          </button>
          {running && onPauseRunning && (
            <button
              onClick={() => onPauseRunning(t.id)}
              className="btn-outline text-sm"
              title={tr('home.paused.pauseTitle')}
            >
              <Pause size={12} /> {tr('home.paused.pause')}
            </button>
          )}
          {onArchiveFromList && (
            <button
              onClick={() => onArchiveFromList(t.id)}
              className="btn-outline text-sm"
              title={tr('home.paused.archiveTitleAttr')}
            >
              <Save size={12} /> {tr('home.paused.saveAndClose')}
            </button>
          )}
          <button
            onClick={() => setConfirmDelete(t)}
            className="btn-ghost text-xs text-rose hover:bg-rose-soft"
            title={tr(running ? 'home.paused.endAndDeleteTitle' : 'home.paused.deletePausedTitle')}
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-6 space-y-5">
      {/* Ongoing tournaments — active (open now) + parked ones that are running. */}
      {(activeTournament || runningList.length > 0) &&
        (() => {
          const n = (activeTournament ? 1 : 0) + runningList.length;
          return (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div
                  className="flex items-center justify-center w-7 h-7 rounded-full"
                  style={{ background: '#dcfce7', border: '1px solid #86efac' }}
                >
                  <Play size={13} style={{ color: '#15803d' }} />
                </div>
                <div className="flex-1">
                  <div
                    className="font-mono text-[10px] font-bold tracking-wider uppercase"
                    style={{ color: '#15803d' }}
                  >
                    {n === 1
                      ? tr('home.paused.runningOne')
                      : tr('home.paused.runningMany', { count: String(n) })}
                  </div>
                  <div className="text-[11px] text-ink-3">{tr('home.paused.runningHint')}</div>
                </div>
              </div>
              <div className="space-y-2">
                {activeTournament &&
                  (() => {
                    const t = activeTournament;
                    const tt = getTournamentType(t.config);
                    const isBroken =
                      !Array.isArray(t.matches) ||
                      !Array.isArray(t.groups) ||
                      t.matches.length === 0 ||
                      t.groups.length === 0;
                    const completedCount = (t.matches || []).filter((m) => m.completed).length;
                    const totalCount = (t.matches || []).length;
                    const isAmericano =
                      t.type === 'americano' ||
                      t.type === 'mexicano' ||
                      t.type === 'mixicano' ||
                      t.type === 'team_mexicano' ||
                      t.type === 'mix_americano' ||
                      t.type === 'club_americano' ||
                      t.type === 'club_team_americano' ||
                      t.type === 'club_mexicano';
                    const statusLabel = isBroken
                      ? tr('home.paused.status.incomplete')
                      : t.status === 'setup' || t.status === 'ready'
                        ? tr('home.paused.status.ready')
                        : t.status === 'running' || t.status === 'playing'
                          ? tr('home.paused.status.running')
                          : t.status === 'paused'
                            ? tr('home.paused.status.paused')
                            : t.status === 'finals'
                              ? tr('home.paused.status.finals')
                              : t.status === 'done'
                                ? tr('home.paused.status.done')
                                : tr('home.paused.status.active');
                    return (
                      <div
                        className="card-elev p-4 border-l-4 flex items-center gap-3 flex-wrap"
                        style={{
                          borderLeftColor: tt.color,
                          background: `linear-gradient(135deg, ${tt.color}14 0%, ${tt.color}2e 100%)`,
                          outline: '2px solid #84cc16',
                        }}
                      >
                        <span
                          className="font-mono text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0"
                          style={{ background: tt.color + '40', color: '#0f1611' }}
                          title={tt.label}
                        >
                          {tt.abbrev}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-base font-semibold text-ink truncate flex items-center gap-2">
                            {t.config?.name || tr('home.paused.untitled')}
                            <span
                              className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                              style={{
                                background: '#dcfce7',
                                color: '#15803d',
                                border: '1px solid #86efac',
                              }}
                            >
                              {tr('home.paused.openNow')}
                            </span>
                          </div>
                          <div className="text-[11px] text-ink-3 font-mono flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 items-center">
                            <span>
                              {tr('home.paused.players', {
                                count: String(t.players?.length || 0),
                              })}
                            </span>
                            <span>
                              {totalCount > 0
                                ? tr('home.paused.matchesPlayed', {
                                    done: String(completedCount),
                                    total: String(totalCount),
                                  })
                                : t.groups?.length
                                  ? tr('home.paused.groups', { count: String(t.groups.length) })
                                  : tr('home.paused.notStarted')}
                            </span>
                            {isAmericano && t.currentRound > 0 && (
                              <span>
                                {tr('home.paused.round', { round: String(t.currentRound) })}
                              </span>
                            )}
                            <span
                              className="inline-flex items-center gap-1 font-bold"
                              style={{ color: isBroken ? '#b45309' : '#15803d' }}
                            >
                              <span
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: 999,
                                  background: isBroken ? '#f59e0b' : '#22c55e',
                                  display: 'inline-block',
                                }}
                              />
                              {statusLabel}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          <button onClick={onOpenActive} className="btn-lime text-sm">
                            <Play size={12} /> {tr('home.paused.open')}
                          </button>
                          <button
                            onClick={() => {
                              if (t.status === 'done') {
                                onArchiveActive?.();
                              } else {
                                setConfirmArchiveActive(t);
                              }
                            }}
                            className="btn-outline text-sm"
                          >
                            <Save size={12} /> {tr('home.paused.saveAndClose')}
                          </button>
                          <button
                            onClick={onDeleteActive}
                            // An icon-only button had no name; it opens the
                            // delete confirmation, which gives focus back here.
                            aria-label={tr('home.paused.endAndDeleteTitle')}
                            className="btn-ghost text-xs text-rose hover:bg-rose-soft"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                {runningList.map((t) => renderCard(t, true))}
              </div>
            </div>
          );
        })()}

      {/* Paused tournaments */}
      {pausedList.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div
              className="flex items-center justify-center w-7 h-7 rounded-full"
              style={{ background: '#fef9c3', border: '1px solid #fde047' }}
            >
              <Pause size={13} style={{ color: '#a16207' }} />
            </div>
            <button
              onClick={() => setPausedOpen((o) => !o)}
              className="flex-1 flex items-center gap-1.5 text-left"
              title={tr(pausedOpen ? 'home.paused.collapseTitle' : 'home.paused.expandTitle')}
            >
              {pausedOpen ? (
                <ChevronDown size={14} style={{ color: '#a16207' }} />
              ) : (
                <ChevronRight size={14} style={{ color: '#a16207' }} />
              )}
              <span>
                <span
                  className="block font-mono text-[10px] font-bold tracking-wider uppercase"
                  style={{ color: '#a16207' }}
                >
                  {pausedList.length === 1
                    ? tr('home.paused.pausedOne')
                    : tr('home.paused.pausedMany', { count: String(pausedList.length) })}
                </span>
                <span className="block text-[11px] text-ink-3">
                  {tr(pausedOpen ? 'home.paused.resumeAnytime' : 'home.paused.tapToShow')}
                </span>
              </span>
            </button>
            {pausedOpen && count >= 2 && (
              <button
                onClick={() => setConfirmDeleteAll(true)}
                className="btn-ghost text-xs text-rose hover:bg-rose-soft flex-shrink-0"
                title={tr('home.paused.deleteAllTitleAttr')}
              >
                <Trash2 size={12} /> {tr('home.paused.deleteAll')}
              </button>
            )}
          </div>
          {pausedOpen && (
            <div className="space-y-2">{pausedList.map((t) => renderCard(t, false))}</div>
          )}
        </div>
      )}

      {/* Confirm deletion */}
      {confirmDelete && (
        // A `Dialog` (#900): named by its title, described by its lead, Tab kept
        // inside, focus given back to the row's delete button. Escape is its
        // «Avbryt» (nothing is deleted), and focus starts there.
        <Dialog
          labelledBy={dlg.deleteTitle}
          describedBy={dlg.deleteBody}
          onClose={() => setConfirmDelete(null)}
          showClose={false}
          backdropClassName=""
          backdropStyle={CONFIRM_BACKDROP_STYLE}
          className="card-elev p-5 max-w-md w-full overflow-y-auto max-h-[92vh]"
          style={{ maxHeight: 'calc(100dvh - 2rem)' }}
        >
          <h3 id={dlg.deleteTitle} className="font-display text-lg font-semibold mb-2">
            {tr('home.paused.deleteConfirmTitle')}
          </h3>
          <p id={dlg.deleteBody} className="text-sm text-ink-2 mb-4">
            {tr('home.paused.deleteLeadPre')}{' '}
            <strong>{confirmDelete.config?.name || tr('home.paused.theTournament')}</strong>
            {tr('home.paused.deleteLeadPost')}
          </p>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
              className="btn-ghost text-sm"
            >
              {tr('home.common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => {
                onDelete(confirmDelete.id);
                setConfirmDelete(null);
              }}
              className="btn-danger text-sm"
            >
              <Trash2 size={12} /> {tr('home.common.delete')}
            </button>
          </div>
        </Dialog>
      )}
      {/* Confirm «save and close» of the open, unfinished tournament */}
      {confirmArchiveActive && (
        // A `Dialog` (#900), as the one above. Escape is «Avbryt»: nothing is
        // archived.
        <Dialog
          labelledBy={dlg.archiveTitle}
          describedBy={dlg.archiveBody}
          onClose={() => setConfirmArchiveActive(null)}
          showClose={false}
          backdropClassName=""
          backdropStyle={CONFIRM_BACKDROP_STYLE}
          className="card-elev p-5 max-w-md w-full overflow-y-auto max-h-[92vh]"
          style={{ maxHeight: 'calc(100dvh - 2rem)' }}
        >
          <h3
            id={dlg.archiveTitle}
            className="font-display text-lg font-semibold mb-2 flex items-center gap-2"
          >
            <AlertCircle size={18} style={{ color: '#d97706' }} aria-hidden="true" />{' '}
            {tr('home.paused.archiveConfirmTitle', {
              name: confirmArchiveActive.config?.name || tr('home.paused.aTournament'),
            })}
          </h3>
          <p id={dlg.archiveBody} className="text-sm text-ink-2 mb-3">
            {tr('home.paused.archiveLeadPre')}{' '}
            <strong>{tr('home.paused.archiveUnfinished')}</strong>
            {tr('home.paused.archiveLeadMid')} <strong>{tr('home.paused.archiveWord')}</strong>
            {tr('home.paused.archiveLeadPost')}
          </p>
          <div
            className="p-2.5 rounded-lg text-[11px] mb-4"
            style={{ background: '#fef9c3', color: '#854d0e' }}
          >
            {tr('home.paused.archiveNote')}
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setConfirmArchiveActive(null)}
              className="btn-ghost text-sm"
            >
              {tr('home.common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => {
                if (onArchiveActive) onArchiveActive();
                setConfirmArchiveActive(null);
              }}
              className="btn-outline text-sm"
            >
              <Save size={12} /> {tr('home.paused.archiveConfirm')}
            </button>
          </div>
        </Dialog>
      )}
      {/* Confirm deletion of ALL — requires extra confirmation */}
      {confirmDeleteAll && (
        // A `Dialog` (#900), as the ones above. Escape is «Avbryt»: nothing is
        // deleted.
        <Dialog
          labelledBy={dlg.deleteAllTitle}
          describedBy={dlg.deleteAllBody}
          onClose={() => setConfirmDeleteAll(false)}
          showClose={false}
          backdropClassName=""
          backdropStyle={CONFIRM_BACKDROP_STYLE}
          className="card-elev p-5 max-w-md w-full overflow-y-auto max-h-[92vh]"
          style={{ maxHeight: 'calc(100dvh - 2rem)' }}
        >
          <h3
            id={dlg.deleteAllTitle}
            className="font-display text-lg font-semibold mb-2 flex items-center gap-2"
          >
            <AlertCircle size={18} className="text-rose" aria-hidden="true" />{' '}
            {tr('home.paused.deleteAllConfirmTitle')}
          </h3>
          <p id={dlg.deleteAllBody} className="text-sm text-ink-2 mb-3">
            {tr('home.paused.deleteAllLeadPre')}{' '}
            <strong>{tr('home.paused.deleteAllCount', { count: String(count) })}</strong>{' '}
            {tr('home.paused.deleteAllLeadPost')}
          </p>
          <div className="p-2.5 rounded-lg bg-rose-soft text-[11px] text-rose mb-4">
            {tr('home.paused.deleteAllWarning')}
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setConfirmDeleteAll(false)}
              className="btn-ghost text-sm"
            >
              {tr('home.common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => {
                if (onDeleteAll) onDeleteAll();
                setConfirmDeleteAll(false);
              }}
              className="btn-danger text-sm"
            >
              <Trash2 size={12} /> {tr('home.paused.deleteAllCta', { count: String(count) })}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
